import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFavorites, updateFavorites } from '@apis/preferences';

const FAVORITES_KEY = ['favorites'] as const;

/**
 * Per-user favorited portfolio projects, synced via the preferences backend.
 * The toggle mutates optimistically against the ['favorites'] cache and
 * invalidates on settle so the server stays the source of truth.
 */
export function useFavorites() {
  const queryClient = useQueryClient();

  const { data: favoriteIds = [] } = useQuery({
    queryKey: FAVORITES_KEY,
    queryFn: ({ signal }) => fetchFavorites(signal),
  });

  const { mutate } = useMutation({
    mutationFn: (ids: string[]) => updateFavorites(ids),
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: FAVORITES_KEY });
      const previous = queryClient.getQueryData<string[]>(FAVORITES_KEY);
      queryClient.setQueryData<string[]>(FAVORITES_KEY, ids);
      return { previous };
    },
    onError: (_err, _ids, context) => {
      // Roll back to the snapshot taken before the optimistic write — but only
      // if one was captured. If the toggle fired before the query first
      // resolved (no snapshot), leave the cache alone rather than wiping it to
      // `[]`; onSettled's invalidation re-syncs from the server.
      if (context?.previous !== undefined) {
        queryClient.setQueryData<string[]>(FAVORITES_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: FAVORITES_KEY });
    },
  });

  const isFavorite = useCallback((id: string) => favoriteIds.includes(id), [favoriteIds]);

  const toggleFavorite = useCallback(
    (id: string) => {
      const next = favoriteIds.includes(id)
        ? favoriteIds.filter((favId) => favId !== id)
        : [...favoriteIds, id];
      mutate(next);
    },
    [favoriteIds, mutate],
  );

  return { favoriteIds, isFavorite, toggleFavorite };
}
